import { mfaSchema, signInSchema, type Copyright, type MfaInput, type SignInInput, type SignInResult } from '@smarton-pbx/shared';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';
import { Field, Notice } from '../components/ui';
import { t } from '../i18n';
import { useSubmit } from '../lib/hooks';
import { AuthLayout } from './AuthLayout';

interface Props {
  name: string;
  logoUrl?: string | null;
  copyright?: Copyright | null;
  signIn: (input: SignInInput) => Promise<SignInResult>;
  verify: (input: MfaInput) => Promise<unknown>;
  onSignedIn: () => void;
}

/** Email and password, then (for admins) the authenticator code; first time, with set-up. */
export function SignIn({ name, logoUrl, copyright, signIn, verify, onSignedIn }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<SignInResult | null>(null);
  const form = useSubmit();

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    const result = await form.submit(signInSchema, { email, password }, signIn);
    if (!result) return;
    if (result.status === 'signed-in') onSignedIn();
    else setStep(result);
  }

  return (
    <AuthLayout name={name} logoUrl={logoUrl} copyright={copyright}>
      {step === null ? (
        <>
          <div className="auth-title">
            <h1>{t('Sign in to {name}', { name })}</h1>
            <p>{t('Welcome back. Use the email your administrator set up for you.')}</p>
          </div>
          <form className="stack" onSubmit={submitPassword} noValidate>
            <Field label={t('Email')} error={form.fields.email}>
              <input
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                data-testid="email"
              />
            </Field>
            <Field label={t('Password')} error={form.fields.password}>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password"
              />
            </Field>
            {form.error && <Notice kind="error">{form.error}</Notice>}
            <button type="submit" disabled={form.busy} data-testid="sign-in" style={{ height: 42 }}>
              {t('Sign in')} <ArrowRight size={16} aria-hidden />
            </button>
          </form>
        </>
      ) : (
        <SecondFactor step={step} verify={verify} onSignedIn={onSignedIn} />
      )}
    </AuthLayout>
  );
}

function SecondFactor({ step, verify, onSignedIn }: { step: SignInResult; verify: Props['verify']; onSignedIn: () => void }) {
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string>();
  const form = useSubmit();
  const setup = step.status === 'mfa-setup-required';

  useEffect(() => {
    if (step.status === 'mfa-setup-required') void QRCode.toDataURL(step.otpauthUri, { margin: 1, width: 320 }).then(setQr);
  }, [step]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (await form.submit(mfaSchema, { code: code.replace(/\s/g, '') }, verify)) onSignedIn();
  }

  return (
    <>
      <div className="auth-title">
        <div className="icon-badge">
          <ShieldCheck size={22} aria-hidden />
        </div>
        <h1>{setup ? t('Set up 2-step sign-in') : t('Enter your code')}</h1>
        <p>{setup ? t('Your account needs a second step at sign-in. It takes a minute.') : t('Open your authenticator app and enter the 6-digit code.')}</p>
      </div>
      <form className="stack" onSubmit={submit} noValidate>
        {step.status === 'mfa-setup-required' && (
          <>
            <ol className="steps">
              <li>{t('Open an authenticator app, such as Google Authenticator, Microsoft Authenticator or 1Password.')}</li>
              <li>{t('Scan this code, or type in the key below it.')}</li>
              <li>{t('Enter the 6-digit code the app shows.')}</li>
            </ol>
            <div className="qr-box">
              {qr ? <img className="qr" src={qr} alt={t('QR code for your authenticator app')} /> : <div className="qr" />}
              <div className="stack" style={{ gap: '0.3rem' }}>
                <span className="subtle">{t('Key')}</span>
                <span className="secret" data-testid="totp-secret">
                  {step.secret}
                </span>
              </div>
            </div>
          </>
        )}
        <Field label={t('Code')} error={form.fields.code}>
          <input
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            data-testid="code"
          />
        </Field>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        <button type="submit" disabled={form.busy} data-testid="verify" style={{ height: 42 }}>
          {t('Continue')} <ArrowRight size={16} aria-hidden />
        </button>
      </form>
    </>
  );
}
